import { requestGQL } from 'common/graphQlClient';

import { MutatePatchTwistedPair, PATCH_TWISTED_PAIR } from '../api';
import { Outlet } from '../Outlet';
import { TwistedPair } from '../TPOutlet';
import { queryClient } from '../../../common/queryClient';
import type { GetPropertyConnectionResponse, LinkData } from '../../connection/api';
import { useOfflineMutation } from '../../../common/offline';

export const parseTwistedPairToRequest =
  (outletId: Outlet['id']) =>
  (twistedPair: TwistedPair) =>
  (tpForm: Partial<TwistedPair>): MutatePatchTwistedPair => {
    return {
      id: twistedPair.id,
      category: tpForm.category ?? twistedPair.category ?? '',
      shielding: tpForm.shielding ?? twistedPair.shielding ?? '',
      length: tpForm.length ?? twistedPair.length ?? 0,
      from: tpForm.from ?? { portId: twistedPair.from.portId, nodeId: twistedPair.from.nodeId },
      to: tpForm.to ?? { portId: twistedPair.to.portId, nodeId: outletId },
    };
  };

type UsePatchTwistedPairOptions = {
  onOfflineQueued?: () => void;
  onSuccess?: () => void;
};

export const usePatchTwistedPair = (
  outletId: string,
  propertyId: number,
  twistedPair: TwistedPair,
  options: UsePatchTwistedPairOptions = {},
) => {
  const applyOptimisticUpdate: (v: any) => void = (optimisticData: MutatePatchTwistedPair) => {
    queryClient.setQueryData<GetPropertyConnectionResponse | undefined>(['propertyConnection', propertyId], (old) => {
      if (!old?.propertyById?.connectionByConnectionId.serialized2) return old;
      const updatedTpIdx = old.propertyById.connectionByConnectionId.serialized2.linkDataArray.findIndex(
        ({ id }) => optimisticData.id === id,
      );
      if (updatedTpIdx < 0) return old;
      const oldTpData = old.propertyById.connectionByConnectionId.serialized2.linkDataArray[updatedTpIdx];
      const oldCloned = structuredClone(old);
      (oldCloned.propertyById.connectionByConnectionId.serialized2.linkDataArray as Array<LinkData>)[updatedTpIdx] = {
        ...oldTpData,
        from: optimisticData.from.nodeId,
        fromPort: optimisticData.from.portId,
        to: optimisticData.to.nodeId,
        toPort: optimisticData.to.portId,
        cfObject: {
          ...(oldTpData.cfObject ?? {}),
          length: optimisticData.length ?? oldTpData.cfObject?.length,
          category: optimisticData.category ?? oldTpData.cfObject?.category,
          shielding: optimisticData.shielding ?? oldTpData.cfObject?.shielding,
        },
      };

      return oldCloned;
    });
  };

  const { data, mutate, mutateAsync, isLoading, isError, isSuccess, isOnline, pendingCount, wasQueuedOffline } =
    useOfflineMutation<string, unknown, Partial<TwistedPair>>({
      mutationFn: async (payload) =>
        requestGQL(PATCH_TWISTED_PAIR, {
          propertyId,
          twistedPair: parseTwistedPairToRequest(outletId)(twistedPair)(payload),
        }),
      mutationName: 'patchTwistedPair',
      entity: 'twistedPair',
      operationType: 'UPDATE',
      propertyId,
      invalidateKeys: [['propertyConnection', propertyId]],
      getOptimisticData: (v) => v,
      applyOptimisticUpdate,
      onOfflineQueued: options.onOfflineQueued,
      onSuccess: options.onSuccess,
    });

  return {
    data,
    mutate: ({ tpForm }: { tpForm: Partial<TwistedPair> }, opts?: any) =>
      mutate(parseTwistedPairToRequest(outletId)(twistedPair)(tpForm) as any, opts),
    mutateAsync: ({ tpForm }: { tpForm: Partial<TwistedPair> }, opts?: any) =>
      mutateAsync(parseTwistedPairToRequest(outletId)(twistedPair)(tpForm) as any, opts),
    isLoading,
    isError,
    isSuccess,
    isOnline,
    pendingCount,
    wasQueuedOffline,
  };
};
